"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import WishlistToast from "@components/atoms/WishlistToast";
import WishlistLimitModal from "@components/molecules/WishlistLimitModal";
import { useWishlist, TypeWishlistCar } from "@context/WishlistContext";
import { useWishlistToast } from "@hooks/useWishlistToast";
import { useHasBottomNav } from "@hooks/useHasBottomNav";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { getCarAvailability } from "@utils/carAvailability";

interface WishlistHandlerProps {
  children:
    | ((props: {
        handleWishlistToggle: (
          brand_name: string,
          model_name: string,
          car: TypeWishlistCar
        ) => Promise<void>;
        isCarWishlisted: (carId: string) => boolean;
      }) => React.ReactNode)
    | React.ReactNode;
  cityName?: any;
}

const WishlistHandler = ({ children, cityName }: WishlistHandlerProps) => {
  const router = useRouter();
  const hasBottomNav = useHasBottomNav();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [pendingWishlistCar, setPendingWishlistCar] = useState<TypeWishlistCar | null>(null);
  const [pageName, setPageName] = useState<string>(PAGE_TYPES.BUY_HOMEPAGE);

  useEffect(() => {
    // Safely access window object on client side
    if (typeof window !== "undefined") {
      const pageLink = window.location.href;
      const pageNameValue = pageLink.includes("collection")
        ? PAGE_TYPES.COLLECTION_PAGE
        : PAGE_TYPES.BUY_HOMEPAGE; 
      setPageName(pageNameValue);
    }
  }, []);

  const {
    wishlistItems,
    toggleWishlistItem,
    replaceWishlistItem,
    isCarWishlisted,
  } = useWishlist();
  const { toast, showToast, hideToast } = useWishlistToast();

  const handleWishlistToggle = async (
    brand_name: string,
    model_name: string,
    car: TypeWishlistCar
  ) => {
    const isCurrentlyWishlisted = isCarWishlisted(car.id);

    if (isCurrentlyWishlisted) {
      const result = await toggleWishlistItem(car);
      if (result.success) {
        showToast(car, false);
      }
    } else {
      const result = await toggleWishlistItem(car);
      if (!result.success) {
        if (result.limitReached) {
          setPendingWishlistCar(car);
          setShowLimitModal(true);
        }
      }
      else{
        await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
            pageType: pageName,
            additionalProps: {
              action_type: "tap_btn",
              action_name: "tap_btn_wishlist_success",
              car_availability: getCarAvailability(car?.status),
              vehicle_make: brand_name,
              vehicle_model: model_name,
              city_name: cityName,
            },
        });
      }
      showToast(car, true);
    }
  };

  const handleUndo = async () => {
    if (toast.lastAction && toast.show) {
      const car = wishlistItems.find(
        (item) => item.name === toast.lastAction?.carName
      );
      if (car) {
        const success = await toggleWishlistItem(car);
        if (success) {
          hideToast();
        }
      }
    }
  };

  const handleViewWishlist = () => {
    hideToast();
    router.push("/wishlist");
  };

  const handleReplace = async (oldCarId: string, newCar: TypeWishlistCar) => {
    const success = await replaceWishlistItem(oldCarId, newCar);
    if (success) {
      showToast(newCar, true);
      setShowLimitModal(false);
    }
  };

  return (
    <>
      {typeof children === "function"
        ? children({ handleWishlistToggle, isCarWishlisted })
        : children}

      <WishlistToast
        isVisible={toast.show}
        isAdding={toast.isAdding}
        carMake={toast.carMake}
        carName={toast.carName}
        hasBottomNav={hasBottomNav}
        onViewWishlist={handleViewWishlist}
        onUndo={handleUndo}
      />

      {showLimitModal && pendingWishlistCar && (
        <WishlistLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          wishlistedCars={wishlistItems}
          onReplace={handleReplace}
          newCar={pendingWishlistCar}
        />
      )}
    </>
  );
};

export default WishlistHandler;
